// build trigger: no visible changes
import { useState } from "react";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { ChevronDown, ChevronRight, Mail, Menu, Phone, Search, X } from "lucide-react";
import { MenuProvider, useMenu, type MenuItem } from "@/integrations/wordpress/WordPressMenuProvider";
interface NavLinkItem {
  id?: string | number;
  label: string;
  href?: string;
  children?: MenuItem[];
}
const fallbackLinks: NavLinkItem[] = [{
  label: "Solutions",
  href: "/SolutionsPage"
}, {
  label: "Products",
  href: "/ProductsPage"
}, {
  label: "Industries",
  href: "/IndustriesPage"
}, {
  label: "Services",
  href: "/ServicesPage"
}, {
  label: "Resources",
  href: "/ResourcesPage"
}, {
  label: "About",
  href: "/AboutPage"
}];
function HeaderContent() {
  const [searchOpen, setSearchOpen] = useState(false);
  const [openMobileSubmenus, setOpenMobileSubmenus] = useState<Record<string, boolean>>({});
  const {
    menuItems
  } = useMenu();
  const navLinks: NavLinkItem[] = menuItems && menuItems.length > 0 ? menuItems : fallbackLinks;
  const toggleMobileSubmenu = (label: string) => {
    setOpenMobileSubmenus(prev => ({
      ...prev,
      [label]: !prev[label]
    }));
  };
  return <header id="header" className="w-full sticky top-0 z-50 bg-white border-b border-[#dbe5eb]/60" style={{
    fontFamily: 'Inter, Arial, Helvetica, sans-serif'
  }}>
      <div className="w-full bg-[#0D2C46] text-[#DBE5EB] text-[12px] md:text-[13px] py-2 border-b border-[#123D5F]">
        <div className="page-container flex flex-wrap items-center justify-between gap-y-2">
          <p className="hidden md:block m-0 font-normal leading-tight text-[#DBE5EB]">
            Local application and service support across Ireland and the UK.
          </p>
          <div className="flex flex-wrap items-center gap-x-5 gap-y-1 ml-auto text-[12px] md:text-[13px]">
            {/* Canonical tel hrefs verified 2026-09-01 */}
            <div className="flex items-center gap-2">
              <Badge variant="secondary" className="bg-[#123D5F] hover:bg-[#123D5F] text-white text-[11px] font-semibold px-1.5 py-0.5 rounded-md border-none leading-none">
                IE
              </Badge>
              <Link to="tel:+35351392391" className="flex items-center gap-1.5 text-[#DBE5EB] hover:text-white transition-colors duration-150">
                <Phone className="w-3.5 h-3.5" />
                <span>+353 (0)51 392 391</span>
              </Link>
              <Link to="mailto:info@antech.ie" className="flex items-center gap-1.5 text-[#DBE5EB] hover:text-white transition-colors duration-150">
                <Mail className="w-3.5 h-3.5" />
                <span>info@antech.ie</span>
              </Link>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant="secondary" className="bg-[#123D5F] hover:bg-[#123D5F] text-white text-[11px] font-semibold px-1.5 py-0.5 rounded-md border-none leading-none">
                UK
              </Badge>
              <Link to="tel:+442034689813" className="flex items-center gap-1.5 text-[#DBE5EB] hover:text-white transition-colors duration-150">
                <Phone className="w-3.5 h-3.5" />
                <span>+44 (0)20 346 898 13</span>
              </Link>
              <Link to="mailto:info@antechsolutions.co.uk" className="flex items-center gap-1.5 text-[#DBE5EB] hover:text-white transition-colors duration-150">
                <Mail className="w-3.5 h-3.5" />
                <span>info@antechsolutions.co.uk</span>
              </Link>
            </div>
          </div>
        </div>
      </div>

      <div className="page-container py-3">
        <div className="flex items-center justify-between gap-4">
          <Link to="/" className="flex items-center flex-shrink-0" aria-label="Antech Solutions home">
            <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/Antech_Orange_no_background.5a1df0bb.png" alt="Antech Solutions Ltd." className="h-[3rem] md:h-[3.6rem] w-auto object-contain" style={{
            backgroundColor: 'rgba(0, 0, 0, 0)',
            border: '0px solid rgb(219, 229, 235)',
            boxShadow: 'none'
          }} />
          </Link>

          <nav className="hidden lg:flex items-center gap-5 xl:gap-6" aria-label="Main Navigation">
            {navLinks.map((item, idx) => {
            const hasChildren = Boolean(item.children && item.children.length > 0);
            return <div key={item.label || idx} data-index={idx} className="relative group py-2">
                  <div className="flex items-center gap-1">
                    <Link to={item.href || "#"} className="text-[#0D2C46] hover:text-[#F26522] text-[15.2px] font-medium leading-[22.8px] transition-colors duration-150 flex items-center gap-1">
                      <span>{item.label}</span>
                      {hasChildren && <ChevronDown className="w-3.5 h-3.5 text-[#5b6b78] group-hover:text-[#F26522] transition-transform duration-200 group-hover:rotate-180" />}
                    </Link>
                  </div>

                  {hasChildren && item.children && <div className="absolute top-full left-0 pt-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible translate-y-1 group-hover:translate-y-0 transition-all duration-200 z-50 pointer-events-none group-hover:pointer-events-auto">
                      <div className="bg-white rounded-lg shadow-lg border border-[#dbe5eb]/80 py-2 px-1 min-w-[240px] max-w-xs">
                        {item.children.map((child, childIdx) => <Link key={child.label || childIdx} data-index={childIdx} to={child.href || "#"} className="block px-3.5 py-2 text-sm font-medium text-[#0D2C46] hover:text-[#F26522] hover:bg-slate-50 rounded-md transition-colors duration-150">
                            {child.label}
                          </Link>)}
                      </div>
                    </div>}
                </div>;
          })}
          </nav>

          <div className="flex items-center gap-3">
            {searchOpen && <div className="hidden sm:flex items-center relative animate-in fade-in duration-200">
                <Input type="text" placeholder="Search solutions, products..." className="w-48 xl:w-60 h-10 pr-8 text-sm border-[#dbe5eb] focus-visible:ring-[#0a5e9a]" autoFocus />
                <Button variant="ghost" size="icon" className="absolute right-1 w-7 h-7 text-[#5b6b78] hover:text-[#0d2c46]" onClick={() => setSearchOpen(false)}>
                  <X className="w-4 h-4" />
                </Button>
              </div>}

            <Button variant="ghost" size="icon" onClick={() => setSearchOpen(!searchOpen)} className="w-10 h-10 rounded-full text-[#0D2C46] hover:text-[#F26522] hover:bg-slate-100 transition-colors" aria-label="Search">
              <Search className="w-5 h-5" />
            </Button>

            <Button asChild className="bg-[#F26522] hover:bg-[#ff7a2e] text-white font-semibold text-[15px] h-11 px-5 rounded-md transition-all duration-200 hover:scale-105">
              <Link to="/DiscussYourApplicationPage">Discuss Your Application</Link>
            </Button>

            <Sheet>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="lg:hidden text-[#0D2C46]" aria-label="Open menu">
                  <Menu className="w-6 h-6" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[300px] sm:w-[350px] bg-white p-6">
                <SheetHeader className="text-left mb-6">
                  <SheetTitle className="text-lg font-bold text-[#0D2C46]">Menu</SheetTitle>
                </SheetHeader>
                <div className="flex flex-col gap-4 overflow-y-auto max-h-[calc(100vh-140px)] pr-1">
                  {navLinks.map((item, idx) => {
                  const hasChildren = Boolean(item.children && item.children.length > 0);
                  const isOpen = Boolean(openMobileSubmenus[item.label]);
                  return <div key={item.label || idx} data-index={idx} className="border-b border-[#dbe5eb] pb-3">
                        {hasChildren ? <div>
                            <Button type="button" variant="ghost" onClick={() => toggleMobileSubmenu(item.label)} className="flex items-center justify-between w-full h-auto p-0 text-left py-1 text-[#0D2C46] font-medium text-base hover:text-[#F26522] hover:bg-transparent transition-colors shadow-none">
                              <span>{item.label}</span>
                              <ChevronDown className={`w-4 h-4 text-[#5b6b78] transition-transform duration-200 ${isOpen ? "rotate-180 text-[#F26522]" : ""}`} />
                            </Button>
                            {isOpen && item.children && <div className="pl-3 pt-2 pb-1 flex flex-col gap-2 animate-in fade-in slide-in-from-top-1 duration-200">
                                {item.children.map((child, childIdx) => <Link key={child.label || childIdx} data-index={childIdx} to={child.href || "#"} className="text-sm font-medium text-[#0D2C46] hover:text-[#F26522] py-1 transition-colors block">
                                    {child.label}
                                  </Link>)}
                              </div>}
                          </div> : <div className="flex items-center justify-between">
                            <Link to={item.href || "#"} className="text-[#0D2C46] font-medium text-base hover:text-[#F26522] transition-colors">
                              {item.label}
                            </Link>
                            <ChevronRight className="w-4 h-4 text-[#5b6b78]" />
                          </div>}
                      </div>;
                })}
                  <div className="mt-4 flex flex-col gap-3">
                    <Button asChild className="w-full bg-[#F26522] hover:bg-[#ff7a2e] text-white font-semibold rounded-md h-11">
                      <Link to="/DiscussYourApplicationPage">Discuss Your Application</Link>
                    </Button>
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </header>;
}
export default function Header() {
  return <MenuProvider menu_id="16">
      <HeaderContent />
    </MenuProvider>;
}